import { readFile, readTextFile } from "@tauri-apps/plugin-fs";

/** Extensions read as plain text, no extraction library needed. */
const PLAIN_TEXT_EXTENSIONS = new Set([
  "txt",
  "md",
  "markdown",
  "csv",
  "json",
  "js",
  "jsx",
  "ts",
  "tsx",
  "py",
  "rs",
  "go",
  "java",
  "c",
  "cpp",
  "h",
  "hpp",
  "cs",
  "rb",
  "php",
  "html",
  "css",
  "scss",
  "yaml",
  "yml",
  "toml",
  "sh",
  "sql",
  "xml",
]);

export function extensionOf(path: string): string {
  const dot = path.lastIndexOf(".");
  return dot === -1 ? "" : path.slice(dot + 1).toLowerCase();
}

export function isSupportedForImport(path: string): boolean {
  const ext = extensionOf(path);
  return ext === "pdf" || ext === "docx" || PLAIN_TEXT_EXTENSIONS.has(ext);
}

async function extractPdf(bytes: Uint8Array): Promise<string> {
  const pdfjsLib = await import("pdfjs-dist");
  const workerSrc = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

  const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
  const pages: string[] = [];
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
    const page = await pdf.getPage(pageNumber);
    const content = await page.getTextContent();
    const items = content.items as { str?: string }[];
    pages.push(items.map((item) => item.str ?? "").join(" "));
  }
  return pages.join("\n\n");
}

async function extractDocx(bytes: Uint8Array): Promise<string> {
  const mammoth = (await import("mammoth")).default;
  const arrayBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value;
}

/**
 * Extracts plain text from a file at an absolute `path`, dispatching on
 * extension: `pdfjs-dist` for PDFs, `mammoth` for `.docx`, a direct read for
 * everything else supported. Both extraction libraries are dynamically
 * imported so they stay out of the main bundle until a document actually
 * needs them.
 */
export async function extractText(path: string): Promise<string> {
  const ext = extensionOf(path);

  if (ext === "pdf") {
    return extractPdf(await readFile(path));
  }
  if (ext === "docx") {
    return extractDocx(await readFile(path));
  }
  if (PLAIN_TEXT_EXTENSIONS.has(ext) || ext === "") {
    return readTextFile(path);
  }

  throw new Error(`Unsupported file type: .${ext}`);
}
